import { useEffect, useMemo } from 'react';
import useCollateral from '../../hooks/useCollateral';
import { useImageProxyState } from '../../hooks/useImageProxy';
import { SimpleCollateralPDF } from '../../components/SimpleCollateralPDF';
import Header from './components/Header';
import BaseCard from './components/BaseCard';

const Collateral = () => {
    const { collateralData, loading, error } = useCollateral();
    
    // Use custom hooks for image proxy conversion (automatic cleanup!)
    const { 
        blobUrl: tenantLogoBlobUrl, 
        convertAndSet: convertTenantLogo 
    } = useImageProxyState();
    
    const { 
        blobUrl: prospectLogoBlobUrl, 
        convertAndSet: convertProspectLogo 
    } = useImageProxyState();

    // Helper function to get metadata
    const getMetadata = () => {
        return collateralData?.data?.[0]?.collateral?.collateral?.metadata || {};
    };

    // Helper function to safely get data from collateral array
    const getCollateralSection = (sectionName) => {
        if (!collateralData?.data?.[0]?.collateral?.collateral?.collateral) {
            return null;
        }
        
        const collateralArray = collateralData.data[0].collateral.collateral.collateral;
        
        const section = collateralArray.find(
            item => item[sectionName]
        );
        
        return section ? section[sectionName] : null;
    };

    // Generate prospect logo URL
    const prospectLogoUrl = useMemo(() => {
        if (!collateralData) return null;
        const metadata = getMetadata();
        const prospectDomain = metadata.domain_of_prospect_company;
        
        if (!prospectDomain) return null;
        
        return `https://img.logo.dev/${prospectDomain}?token=pk_W8m2jis1T_-zblJccAPjbQ&size=79&format=png&retina=true`;
    }, [collateralData]);

    // Convert images when data loads
    useEffect(() => {
        if (collateralData) {
            console.log('🖼️ Converting images using reusable hooks...');
            
            // Convert tenant logo
            if (collateralData.tenantlogo) {
                console.log('Converting tenant logo:', collateralData.tenantlogo);
                convertTenantLogo(collateralData.tenantlogo);
            }
            
            // Convert prospect logo
            if (prospectLogoUrl) {
                console.log('Converting prospect logo:', prospectLogoUrl);
                convertProspectLogo(prospectLogoUrl);
            }
        }
    }, [collateralData, prospectLogoUrl, convertTenantLogo, convertProspectLogo]);

    // Helper function to render array items
    const renderArrayItems = (items, className = "text-gray-700") => {
        if (!items || !Array.isArray(items)) return null;
        return (
            <ul className="list-disc list-inside space-y-1">
                {items.map((item, index) => {
                    // Handle both string items and object items
                    if (typeof item === 'string') {
                        return <li key={index} className={className}>{item}</li>;
                    } else if (typeof item === 'object' && item !== null) {
                        // For objects, render the context and clientName if available
                        if (item.context && item.clientName) {
                            return (
                                <li key={index} className={className}>
                                    <strong>{item.clientName}:</strong> {item.context}
                                </li>
                            );
                        }
                        // For other objects, try to render a meaningful representation
                        return <li key={index} className={className}>{JSON.stringify(item)}</li>;
                    }
                    return <li key={index} className={className}>{String(item)}</li>;
                })}
            </ul>
        );
    };

  

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading collateral...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 text-xl mb-4">⚠️</div>
                    <p className="text-red-600">{error}</p>
                </div>
            </div>
        );
    }
   

    if (!collateralData?.data?.[0]) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600">No collateral data found</p>
                </div>
            </div>
        );
    }

    const metadata = getMetadata();
    const prospectDomain = metadata.domain_of_prospect_company || '';

    return (
        <div className="min-h-screen bg-white">
            {/* PDF Download Button - Only show when data is loaded */}
            {!loading && collateralData && (
                <SimpleCollateralPDF 
                    metadata={metadata}
                    fileName={`${metadata.my_company_name || 'company'}-${metadata.prospect_company_name || 'prospect'}-collateral`}
                    buttonText="Download PDF"
                    loadingText="Generating PDF..."
                    onSuccess={() => console.log('PDF downloaded successfully')}
                    onError={(error) => console.error('PDF download failed:', error)}
                />
            )}
            
            {/* Header */}
           <Header 
                className="bg-white" 
                tenantlogo={tenantLogoBlobUrl || collateralData.tenantlogo} 
                metadata={metadata} 
                prospectLogoUrl={prospectLogoBlobUrl}
           />
           
            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                {getCollateralSection('compelling_benefit_oriented_title') && (
                    <div className="mb-12">
                        <BaseCard className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-8 text-white">
                            <h1 className="text-3xl font-bold mb-4">
                                {getCollateralSection('compelling_benefit_oriented_title')?.primaryBenefit}
                            </h1>
                            <p className="text-xl mb-6">
                                {getCollateralSection('compelling_benefit_oriented_title')?.outcomePromise}
                            </p>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-semibold mb-2">Value Proposition</h3>
                                    <p className="text-blue-100">
                                        {getCollateralSection('compelling_benefit_oriented_title')?.valueProposition}
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold mb-2">Quantifiable Impact</h3>
                                    <p className="text-blue-100">
                                        {getCollateralSection('compelling_benefit_oriented_title')?.quantifiableMetric}
                                    </p>
                                </div>
                            </div>
                        </BaseCard>
                    </div>
                )}

                {/* Problem & Solution Section */}
                {getCollateralSection('problem_opportunity') && getCollateralSection('solution_summary') ? (
                    <div className="grid lg:grid-cols-2 gap-8 mb-12">
                        {/* Problem Section */}
                        <BaseCard className="bg-red-50 border border-red-200 rounded-lg p-6">
                            <h2 className="text-2xl font-bold text-red-800 mb-4">Current Challenge</h2>
                            <div className="space-y-4">
                                <p className="text-red-700">
                                    {getCollateralSection('problem_opportunity')?.corePainPoint}
                                </p>
                                <div>
                                    <h3 className="font-semibold text-red-800 mb-2">Root Causes:</h3>
                                    {renderArrayItems(getCollateralSection('problem_opportunity')?.rootCauses, "text-red-600")}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-red-800 mb-2">Business Impact:</h3>
                                    <p className="text-red-700">
                                        {getCollateralSection('problem_opportunity')?.businessImpact}
                                    </p>
                                </div>
                            </div>
                        </BaseCard>

                        {/* Solution Section */}
                        <BaseCard className="bg-teal-200 border border-teal-200 rounded-lg p-6">
                            <h2 className="text-2xl font-bold text-teal-800 mb-4">Our Solution</h2>
                            <div className="space-y-4">
                                <p className="text-teal-700">
                                    {getCollateralSection('solution_summary')?.solutionOverview}
                                </p>
                                <div>
                                    <h3 className="font-semibold text-teal-800 mb-2">Key Features:</h3>
                                    {renderArrayItems(getCollateralSection('solution_summary')?.keyFeatures, "text-teal-600")}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-teal-800 mb-2">Expected Outcomes:</h3>
                                    {renderArrayItems(getCollateralSection('solution_summary')?.expectedOutcomes, "text-teal-600")}
                                </div>
                            </div>
                        </BaseCard>
                    </div>
                ) : (
                    <>
                        {/* Problem Section Only */}
                        {getCollateralSection('problem_opportunity') && (
                            <div className="mb-12">
                                <BaseCard className="bg-red-50 border border-red-200 rounded-lg p-6">
                                    <h2 className="text-2xl font-bold text-red-800 mb-4">Current Challenge</h2>
                                    <div className="space-y-4">
                                        <p className="text-red-700">
                                            {getCollateralSection('problem_opportunity')?.corePainPoint}
                                        </p>
                                        <div>
                                            <h3 className="font-semibold text-red-800 mb-2">Root Causes:</h3>
                                            {renderArrayItems(getCollateralSection('problem_opportunity')?.rootCauses, "text-red-600")}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-red-800 mb-2">Business Impact:</h3>
                                            <p className="text-red-700">
                                                {getCollateralSection('problem_opportunity')?.businessImpact}
                                            </p>
                                        </div>
                                    </div>
                                </BaseCard>
                            </div>
                        )}

                        {/* Solution Section Only */}
                        {getCollateralSection('solution_summary') && (
                            <div className="mb-12">
                                <BaseCard className="bg-green-50 border border-green-200 rounded-lg p-6">
                                    <h2 className="text-2xl font-bold text-green-800 mb-4">Our Solution</h2>
                                    <div className="space-y-4">
                                        <p className="text-green-700">
                                            {getCollateralSection('solution_summary')?.solutionOverview}
                                        </p>
                                        <div>
                                            <h3 className="font-semibold text-green-800 mb-2">Key Features:</h3>
                                            {renderArrayItems(getCollateralSection('solution_summary')?.keyFeatures, "text-green-600")}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-green-800 mb-2">Expected Outcomes:</h3>
                                            {renderArrayItems(getCollateralSection('solution_summary')?.expectedOutcomes, "text-green-600")}
                                        </div>
                                    </div>
                                </BaseCard>
                            </div>
                        )}
                    </>
                )}

                {/* Current Situation Statement */}
                {getCollateralSection('current_situation_statement') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Current Situation</h2>
                        <BaseCard className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Market Position</h3>
                                    <p className="text-gray-700 mb-4">
                                        {getCollateralSection('current_situation_statement')?.marketPosition}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-2">Baseline Metrics:</h4>
                                        {renderArrayItems(getCollateralSection('current_situation_statement')?.baselineMetrics, "text-gray-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Technology Stack</h3>
                                    <p className="text-gray-700 mb-4">
                                        {getCollateralSection('current_situation_statement')?.technologyStack}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-2">Performance Levels:</h4>
                                        {renderArrayItems(getCollateralSection('current_situation_statement')?.performanceLevels, "text-gray-600")}
                                    </div>
                                </div>
                            </div>
                        </BaseCard>
                    </section>
                )}

                {/* Context - Why and Why Now */}
                {getCollateralSection('context_why_and_why_now') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why This Matters Now</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <BaseCard className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-blue-800 mb-4">Market Drivers</h3>
                                {renderArrayItems(getCollateralSection('context_why_and_why_now')?.marketDrivers, "text-blue-700")}
                                <div className="mt-4">
                                    <h4 className="font-semibold text-blue-800 mb-2">Timing Factors:</h4>
                                    {renderArrayItems(getCollateralSection('context_why_and_why_now')?.timingFactors, "text-blue-600")}
                                </div>
                                </BaseCard>
                            <BaseCard className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-green-800 mb-4">Current Situation</h3>
                                <p className="text-green-700 mb-4">
                                    {getCollateralSection('context_why_and_why_now')?.currentSituation}
                                </p>
                                <div>
                                    <h4 className="font-semibold text-green-800 mb-2">Urgency Triggers:</h4>
                                    {renderArrayItems(getCollateralSection('context_why_and_why_now')?.urgencyTriggers, "text-green-600")}
                                </div>
                            </BaseCard>
                        </div>
                    </section>
                )}

                {/* Key Features & Capabilities */}
                {getCollateralSection('key_features_capabilities') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Features & Capabilities</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <BaseCard className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-blue-800 mb-3">Core Features</h3>
                                {renderArrayItems(getCollateralSection('key_features_capabilities')?.coreFeatures, "text-blue-700")}
                            </BaseCard>
                            <BaseCard className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-green-800 mb-3">Security Features</h3>
                                {renderArrayItems(getCollateralSection('key_features_capabilities')?.securityFeatures, "text-green-700")}
                            </BaseCard>
                            <BaseCard className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-purple-800 mb-3">Automation Functions</h3>
                                {renderArrayItems(getCollateralSection('key_features_capabilities')?.automationFunctions, "text-purple-700")}
                            </BaseCard>
                            <BaseCard className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-orange-800 mb-3">Integration Features</h3>
                                {renderArrayItems(getCollateralSection('key_features_capabilities')?.integrationFeatures, "text-orange-700")}
                            </BaseCard>
                            <BaseCard className="bg-red-50 border border-red-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-red-800 mb-3">Performance Features</h3>
                                {renderArrayItems(getCollateralSection('key_features_capabilities')?.performanceFeatures, "text-red-700")}
                            </BaseCard>
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-indigo-800 mb-3">Scalability Features</h3>
                                {renderArrayItems(getCollateralSection('key_features_capabilities')?.scalabilityFeatures, "text-indigo-700")}
                            </div>
                        </div>
                    </section>
                )}

                {/* Problem-Solution Alignment */}
                {getCollateralSection('problem_solution_alignment') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Problem-Solution Alignment</h2>
                        <BaseCard className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Direct Mapping</h3>
                                    <p className="text-gray-700 mb-4">
                                        {getCollateralSection('problem_solution_alignment')?.directMapping}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-2">Solution Benefits:</h4>
                                        {renderArrayItems(getCollateralSection('problem_solution_alignment')?.solutionBenefits, "text-gray-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Success Criteria</h3>
                                    {renderArrayItems(getCollateralSection('problem_solution_alignment')?.successCriteria, "text-gray-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">Measurement Metrics:</h4>
                                        {renderArrayItems(getCollateralSection('problem_solution_alignment')?.measurementMetrics, "text-gray-600")}
                                    </div>
                                </div>
                            </div>
                        </BaseCard>
                    </section>
                )}

                {/* Benefits Section */}
                {getCollateralSection('high_level_benefits') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Benefits</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <BaseCard className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-blue-800 mb-3">Immediate Wins</h3>
                                {renderArrayItems(getCollateralSection('high_level_benefits')?.immediateWins, "text-blue-700")}
                            </BaseCard>
                            <BaseCard className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-purple-800 mb-3">Strategic Value</h3>
                                <p className="text-purple-700 mb-3">
                                    {getCollateralSection('high_level_benefits')?.strategicValue}
                                </p>
                                <div>
                                    <h4 className="font-semibold text-purple-800 mb-2">Primary Benefits:</h4>
                                    {renderArrayItems(getCollateralSection('high_level_benefits')?.primaryBenefits, "text-purple-600")}
                                </div>
                            </BaseCard>
                            <BaseCard className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-orange-800 mb-3">Operational Efficiency</h3>
                                {renderArrayItems(getCollateralSection('high_level_benefits')?.operationalEfficiencies, "text-orange-700")}
                            </BaseCard>
                        </div>
                        <div className="mt-8 grid md:grid-cols-2 gap-6">
                            <BaseCard className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-green-800 mb-3">Quantifiable Gains</h3>
                                {renderArrayItems(getCollateralSection('high_level_benefits')?.quantifiableGains, "text-green-700")}
                            </BaseCard>
                            <BaseCard className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-indigo-800 mb-3">Competitive Advantages</h3>
                                {renderArrayItems(getCollateralSection('high_level_benefits')?.competitiveAdvantages, "text-indigo-700")}
                            </BaseCard>
                        </div>
                    </section>
                )}

                {/* Golden Quadrant */}
                {getCollateralSection('golden_quadrant') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                            {getCollateralSection('golden_quadrant')?.title}
                        </h2>
                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                            <div className="grid lg:grid-cols-3 gap-8">
                                {/* Quadrant Chart */}
                                <div className="lg:col-span-2">
                                    <div className="relative bg-gray-50 border border-gray-300 rounded-lg p-6 h-96">
                                        {/* Y-axis label */}
                                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-8 -rotate-90 text-sm font-semibold text-gray-700 whitespace-nowrap">
                                            {getCollateralSection('golden_quadrant')?.y_axis?.label}
                                        </div>
                                        
                                        {/* X-axis label */}
                                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-8 text-sm font-semibold text-gray-700">
                                            {getCollateralSection('golden_quadrant')?.x_axis?.label}
                                        </div>
                                        
                                        {/* Quadrant labels */}
                                        <div className="absolute top-2 left-2 text-xs font-semibold text-gray-600">
                                            {getCollateralSection('golden_quadrant')?.quadrants?.top_left?.name}
                                        </div>
                                        <div className="absolute top-2 right-2 text-xs font-semibold text-gray-600">
                                            {getCollateralSection('golden_quadrant')?.quadrants?.top_right?.name}
                                        </div>
                                        <div className="absolute bottom-2 left-2 text-xs font-semibold text-gray-600">
                                            {getCollateralSection('golden_quadrant')?.quadrants?.bottom_left?.name}
                                        </div>
                                        <div className="absolute bottom-2 right-2 text-xs font-semibold text-gray-600">
                                            {getCollateralSection('golden_quadrant')?.quadrants?.bottom_right?.name}
                                        </div>
                                        
                                        {/* Data points */}
                                        {getCollateralSection('golden_quadrant')?.data_points?.map((point, index) => {
                                            // Convert 1-10 scale to percentage for positioning
                                            const xPercent = ((point.x - 1) / 9) * 100;
                                            const yPercent = ((point.y - 1) / 9) * 100;
                                            
                                            return (
                                                <div
                                                    key={index}
                                                    className="absolute transform -translate-x-1/2 -translate-y-1/2"
                                                    style={{
                                                        left: `${xPercent}%`,
                                                        top: `${100 - yPercent}%`, // Invert Y for bottom-left origin
                                                    }}
                                                >
                                                    <div
                                                        className={`rounded-full border-2 ${
                                                            point.highlight 
                                                                ? 'border-yellow-400 shadow-lg animate-pulse' 
                                                                : 'border-gray-400'
                                                        }`}
                                                        style={{
                                                            width: `${point.size}px`,
                                                            height: `${point.size}px`,
                                                            backgroundColor: point.color,
                                                        }}
                                                        title={point.label}
                                                    />
                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 text-xs font-medium text-gray-700 whitespace-nowrap">
                                                        {point.label}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        
                                        {/* Center lines */}
                                        <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-300"></div>
                                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300"></div>
                                    </div>
                                </div>
                                
                                {/* Legend and descriptions */}
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quadrant Descriptions</h3>
                                        <div className="space-y-3">
                                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                                                <h4 className="font-semibold text-yellow-800 text-sm">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.top_right?.name}
                                                </h4>
                                                <p className="text-yellow-700 text-xs mt-1">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.top_right?.description}
                                                </p>
                                            </div>
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                                                <h4 className="font-semibold text-blue-800 text-sm">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.top_left?.name}
                                                </h4>
                                                <p className="text-blue-700 text-xs mt-1">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.top_left?.description}
                                                </p>
                                            </div>
                                            <div className="p-3 bg-green-50 border border-green-200 rounded">
                                                <h4 className="font-semibold text-green-800 text-sm">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.bottom_right?.name}
                                                </h4>
                                                <p className="text-green-700 text-xs mt-1">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.bottom_right?.description}
                                                </p>
                                            </div>
                                            <div className="p-3 bg-red-50 border border-red-200 rounded">
                                                <h4 className="font-semibold text-red-800 text-sm">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.bottom_left?.name}
                                                </h4>
                                                <p className="text-red-700 text-xs mt-1">
                                                    {getCollateralSection('golden_quadrant')?.quadrants?.bottom_left?.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Competitive Positioning</h3>
                                        <div className="space-y-2">
                                            {getCollateralSection('golden_quadrant')?.data_points?.map((point, index) => (
                                                <div key={index} className="flex items-center space-x-2">
                                                    <div
                                                        className="w-4 h-4 rounded-full border border-gray-400"
                                                        style={{ backgroundColor: point.color }}
                                                    />
                                                    <span className="text-sm text-gray-700">{point.label}</span>
                                                    {point.highlight && (
                                                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                                            You
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Unique Differentiation */}
                {getCollateralSection('unique_differentiation_why_you') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Us</h2>
                        <BaseCard className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-8 text-white">
                            <h3 className="text-2xl font-bold mb-4">
                                {getCollateralSection('unique_differentiation_why_you')?.uniqueValueProposition}
                            </h3>
                            <div className="grid md:grid-cols-2 gap-8 mt-6">
                                <div>
                                    <h4 className="text-lg font-semibold mb-3">Expertise Areas</h4>
                                    {renderArrayItems(getCollateralSection('unique_differentiation_why_you')?.expertiseAreas, "text-purple-100")}
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold mb-3">Competitive Advantages</h4>
                                    {renderArrayItems(getCollateralSection('unique_differentiation_why_you')?.competitiveAdvantages, "text-purple-100")}
                                </div>
                            </div>
                            <div className="mt-6">
                                <h4 className="text-lg font-semibold mb-3">Exclusive Capabilities</h4>
                                {renderArrayItems(getCollateralSection('unique_differentiation_why_you')?.exclusiveCapabilities, "text-purple-100")}
                            </div>
                        </BaseCard>
                    </section>
                )}

                {/* Competitive Analysis */}
                {getCollateralSection('competitive_analysis') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Competitive Analysis</h2>
                        <BaseCard className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            {/* Header */}
                            <BaseCard className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                                <div className="grid grid-cols-12 gap-4 text-sm font-semibold text-gray-700">
                                    <div className="col-span-3">Factor</div>
                                    <div className="col-span-3">Our Advantage</div>
                                    <div className="col-span-3">Competitors</div>
                                    <div className="col-span-3">Our Solution</div>
                                </div>
                                </BaseCard>
                            
                            {/* Table Body */}
                            <div className="divide-y divide-gray-200">
                                {getCollateralSection('competitive_analysis')?.factors?.map((factor, index) => (
                                    <div key={index} className="px-6 py-6 hover:bg-gray-50 transition-colors">
                                        <div className="grid grid-cols-12 gap-4">
                                            {/* Factor Name */}
                                            <div className="col-span-3">
                                                <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                                                    {factor.name}
                                                </h3>
                                            </div>
                                            
                                            {/* Our Edge */}
                                            <div className="col-span-3">
                                                <p className="text-sm text-green-700 leading-relaxed">
                                                    {factor.our_edge}
                                                </p>
                                            </div>
                                            
                                            {/* Competitors */}
                                            <div className="col-span-3">
                                                <div className="space-y-2">
                                                    {factor.companies?.competitor?.map((competitor, compIndex) => (
                                                        <div key={compIndex} className="text-sm">
                                                            <div className="font-medium text-red-600">
                                                                {competitor.name}
                                                            </div>
                                                            <div className="text-gray-600 text-xs mt-1">
                                                                {competitor.value}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Our Company */}
                                            <div className="col-span-3">
                                                <p className="text-sm text-blue-700 font-medium leading-relaxed">
                                                    {factor.companies?.our_company}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Customer Concerns */}
                            <div className="bg-blue-50 px-6 py-4 border-t border-gray-200">
                                <h3 className="font-semibold text-blue-800 mb-3">Key Customer Concerns</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    {getCollateralSection('competitive_analysis')?.customer_concerns?.map((concern, index) => (
                                        <div key={index} className="flex items-start space-x-2">
                                            <span className="text-blue-600 mt-1">•</span>
                                            <span className="text-sm text-blue-700">{concern}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Primary Competitors */}
                            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                                <h3 className="font-semibold text-gray-800 mb-2">Primary Competitors</h3>
                                <div className="flex flex-wrap gap-2">
                                    {getCollateralSection('competitive_analysis')?.primary_competitors?.map((competitor, index) => (
                                        <span key={index} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                                            {competitor}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </BaseCard>
                    </section>
                )}

                {/* Implementation Roadmap */}
                {getCollateralSection('implemetaion_roadmap') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Implementation Roadmap</h2>
                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                            <div className="relative">
                                {/* Timeline Line */}
                                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-green-500"></div>
                                
                                {/* Steps */}
                                <div className="space-y-8">
                                    {getCollateralSection('implemetaion_roadmap')?.roadmap?.map((step, index) => (
                                        <div key={index} className="relative flex items-start">
                                            {/* Timeline Dot */}
                                            <div className="relative z-10 flex-shrink-0 w-16 h-16 bg-white border-4 border-blue-500 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                                            </div>
                                            
                                            {/* Content */}
                                            <div className="ml-6 flex-1">
                                                <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {step.implementation_step_name}
                                                        </h3>
                                                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                                            {step.estimated_time}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-700 leading-relaxed">
                                                        {step.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Summary */}
                            <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Implementation Summary</h3>
                                <div className="grid md:grid-cols-3 gap-4 text-sm">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">30 Days</div>
                                        <div className="text-gray-600">Total Timeline</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">6 Phases</div>
                                        <div className="text-gray-600">Implementation Steps</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-purple-600">Overlapping</div>
                                        <div className="text-gray-600">Parallel Execution</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Additional Module */}
                {getCollateralSection('additional_module') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                            {getCollateralSection('additional_module')?.main_heading}
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {getCollateralSection('additional_module')?.sections?.map((section, index) => (
                                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        {section.heading}
                                    </h3>
                                    <h4 className="text-sm font-medium text-blue-600 mb-3">
                                        {section.subheading}
                                    </h4>
                                    <p className="text-gray-700 leading-relaxed">
                                        {section.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* ROI & Financial Impact */}
                {getCollateralSection('roi_calculation') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Financial Impact & ROI</h2>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">ROI Projections</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">ROI Percentage:</span>
                                            <span className="font-semibold text-green-600">
                                                {getCollateralSection('roi_calculation')?.roiPercentage}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Payback Period:</span>
                                            <span className="font-semibold text-blue-600">
                                                {getCollateralSection('payback_period')?.timeToBreakeven}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Initial Investment:</span>
                                            <span className="font-semibold text-gray-800">
                                                {getCollateralSection('investment_cost')?.initialInvestment}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Quantified Benefits</h3>
                                    {getCollateralSection('quantified_value_benefits_deliver')?.measurableBenefits && (
                                        <ul className="space-y-2">
                                            {getCollateralSection('quantified_value_benefits_deliver')?.measurableBenefits.map((benefit, index) => (
                                                <li key={index} className="text-gray-700 flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    {benefit}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Financial Impact - CFO */}
                {getCollateralSection('financial_impact_cfo') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Financial Impact (CFO Perspective)</h2>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-green-800 mb-4">Cost Analysis</h3>
                                    <p className="text-green-700 mb-4">
                                        {getCollateralSection('financial_impact_cfo')?.costAnalysis}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-green-800 mb-2">Cost Savings:</h4>
                                        <p className="text-green-600">
                                            {getCollateralSection('financial_impact_cfo')?.costSavings}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-green-800 mb-4">Revenue Impact</h3>
                                    <p className="text-green-700 mb-4">
                                        {getCollateralSection('financial_impact_cfo')?.revenueImpact}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-green-800 mb-2">ROI Projections:</h4>
                                        <p className="text-green-600">
                                            {getCollateralSection('financial_impact_cfo')?.roiProjections}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Operational Impact - COO */}
                {getCollateralSection('operational_impact_coo') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Operational Impact (COO Perspective)</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-blue-800 mb-4">Process Efficiency</h3>
                                <p className="text-blue-700 mb-4">
                                    {getCollateralSection('operational_impact_coo')?.processEfficiency}
                                </p>
                                <div>
                                    <h4 className="font-semibold text-blue-800 mb-2">Productivity Metrics:</h4>
                                    {renderArrayItems(getCollateralSection('operational_impact_coo')?.productivityMetrics, "text-blue-600")}
                                </div>
                            </div>
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-purple-800 mb-4">Scalability Factors</h3>
                                <p className="text-purple-700 mb-4">
                                    {getCollateralSection('operational_impact_coo')?.scalabilityFactors}
                                </p>
                                <div>
                                    <h4 className="font-semibold text-purple-800 mb-2">Performance Indicators:</h4>
                                    {renderArrayItems(getCollateralSection('operational_impact_coo')?.performanceIndicators, "text-purple-600")}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Sales & Marketing Impact */}
                {getCollateralSection('sales_marketing_impact_cro_cmo') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Sales & Marketing Impact</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-green-800 mb-3">Market Share</h3>
                                <p className="text-green-700">
                                    {getCollateralSection('sales_marketing_impact_cro_cmo')?.marketShare}
                                </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-blue-800 mb-3">Revenue Growth</h3>
                                <p className="text-blue-700">
                                    {getCollateralSection('sales_marketing_impact_cro_cmo')?.revenueGrowth}
                                </p>
                            </div>
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-purple-800 mb-3">Sales Velocity</h3>
                                <p className="text-purple-700">
                                    {getCollateralSection('sales_marketing_impact_cro_cmo')?.salesVelocity}
                                </p>
                            </div>
                        </div>
                    </section>
                )}

                {/* Supporting Evidence */}
                {getCollateralSection('provide_supporting_evidence') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Supporting Evidence</h2>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Industry Benchmarks</h3>
                                    {renderArrayItems(getCollateralSection('provide_supporting_evidence')?.industryBenchmarks, "text-gray-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">Case Study References:</h4>
                                        {renderArrayItems(getCollateralSection('provide_supporting_evidence')?.caseStudyReferences, "text-gray-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Financial Metrics</h3>
                                    {renderArrayItems(getCollateralSection('provide_supporting_evidence')?.financialMetrics, "text-gray-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">Market Statistics:</h4>
                                        {renderArrayItems(getCollateralSection('provide_supporting_evidence')?.marketStatistics, "text-gray-600")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Problem Challenge */}
                {getCollateralSection('problem_challenge') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Problem & Challenge</h2>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-red-800 mb-4">Business Problem</h3>
                                    <p className="text-red-700 mb-4">
                                        {getCollateralSection('problem_challenge')?.businessProblem}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-red-800 mb-2">Root Causes:</h4>
                                        {renderArrayItems(getCollateralSection('problem_challenge')?.rootCauses, "text-red-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-red-800 mb-4">Impact Assessment</h3>
                                    <p className="text-red-700 mb-4">
                                        {getCollateralSection('problem_challenge')?.impactAssessment}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-red-800 mb-2">Stakeholders Affected:</h4>
                                        {renderArrayItems(getCollateralSection('problem_challenge')?.stakeholderAffected, "text-red-600")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Solution Details */}
                {getCollateralSection('solution') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Solution Details</h2>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-green-800 mb-4">Solution Approach</h3>
                                    <p className="text-green-700 mb-4">
                                        {getCollateralSection('solution')?.solutionApproach}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-green-800 mb-2">Key Components:</h4>
                                        {renderArrayItems(getCollateralSection('solution')?.keyComponents, "text-green-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-green-800 mb-4">Implementation Strategy</h3>
                                    <p className="text-green-700 mb-4">
                                        {getCollateralSection('solution')?.implementationStrategy}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-green-800 mb-2">Success Factors:</h4>
                                        {renderArrayItems(getCollateralSection('solution')?.successFactors, "text-green-600")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Results & ROI */}
                {getCollateralSection('results_roi') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Results & ROI</h2>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-blue-800 mb-4">ROI Metrics</h3>
                                    <p className="text-blue-700 mb-4">
                                        {getCollateralSection('results_roi')?.roiMetrics}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-blue-800 mb-2">Quantified Results:</h4>
                                        {renderArrayItems(getCollateralSection('results_roi')?.quantifiedResults, "text-blue-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-blue-800 mb-4">Performance Improvements</h3>
                                    {renderArrayItems(getCollateralSection('results_roi')?.performanceImprovements, "text-blue-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-blue-800 mb-2">Quality Improvements:</h4>
                                        <p className="text-blue-600">
                                            {getCollateralSection('results_roi')?.qualityImprovements}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Implementation Timeline */}
                {getCollateralSection('visual_timeline') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Implementation Timeline</h2>
                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Project Schedule</h3>
                                    <p className="text-gray-700">
                                        {getCollateralSection('visual_timeline')?.projectSchedule}
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Milestones</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {getCollateralSection('visual_timeline')?.milestoneMarkers?.map((milestone, index) => (
                                            <div key={index} className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                                                <span className="text-gray-700">{milestone}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Investment & Cost */}
                {getCollateralSection('investment_cost') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Investment & Cost</h2>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-yellow-800 mb-4">Initial Investment</h3>
                                    <p className="text-yellow-700 mb-4">
                                        {getCollateralSection('investment_cost')?.initialInvestment}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-yellow-800 mb-2">Implementation Costs:</h4>
                                        <p className="text-yellow-600">
                                            {getCollateralSection('investment_cost')?.implementationCosts}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-yellow-800 mb-4">Cost Breakdown</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-yellow-700">Integration Costs:</span>
                                            <span className="text-yellow-600">
                                                {getCollateralSection('investment_cost')?.integrationCosts}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-yellow-700">Training Costs:</span>
                                            <span className="text-yellow-600">
                                                {getCollateralSection('investment_cost')?.trainingCosts}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-yellow-700">Project Management:</span>
                                            <span className="text-yellow-600">
                                                {getCollateralSection('investment_cost')?.projectManagement}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Key Deliverables */}
                {getCollateralSection('key_deliverables') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Deliverables</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-blue-800 mb-4">Primary Outputs</h3>
                                {renderArrayItems(getCollateralSection('key_deliverables')?.primaryOutputs, "text-blue-700")}
                                <div className="mt-4">
                                    <h4 className="font-semibold text-blue-800 mb-2">System Components:</h4>
                                    {renderArrayItems(getCollateralSection('key_deliverables')?.systemComponents, "text-blue-600")}
                                </div>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                <h3 className="text-xl font-semibold text-green-800 mb-4">Documentation</h3>
                                {renderArrayItems(getCollateralSection('key_deliverables')?.documentation, "text-green-700")}
                                <div className="mt-4">
                                    <h4 className="font-semibold text-green-800 mb-2">Training Materials:</h4>
                                    {renderArrayItems(getCollateralSection('key_deliverables')?.trainingMaterials, "text-green-600")}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Scope Definition */}
                {getCollateralSection('scope_definition') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Scope Definition</h2>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Included Elements</h3>
                                    {renderArrayItems(getCollateralSection('scope_definition')?.includedElements, "text-gray-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">Success Criteria:</h4>
                                        {renderArrayItems(getCollateralSection('scope_definition')?.successCriteria, "text-gray-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Excluded Elements</h3>
                                    {renderArrayItems(getCollateralSection('scope_definition')?.excludedElements, "text-gray-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">Project Boundaries:</h4>
                                        <p className="text-gray-600">
                                            {getCollateralSection('scope_definition')?.projectBoundaries}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Value-Based Comparison */}
                {getCollateralSection('value_based_comparison') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Value-Based Comparison</h2>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-purple-800 mb-4">Time to Value</h3>
                                    <p className="text-purple-700 mb-4">
                                        {getCollateralSection('value_based_comparison')?.timeToValue}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-purple-800 mb-2">ROI Comparison:</h4>
                                        <p className="text-purple-600">
                                            {getCollateralSection('value_based_comparison')?.roiComparison}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-purple-800 mb-4">Business Value Metrics</h3>
                                    {renderArrayItems(getCollateralSection('value_based_comparison')?.businessValueMetrics, "text-purple-700")}
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-purple-800 mb-2">Total Cost of Ownership:</h4>
                                        <p className="text-purple-600">
                                            {getCollateralSection('value_based_comparison')?.totalCostOfOwnership}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Justification & Recommendation */}
                {getCollateralSection('justification_recommendation') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Justification & Recommendation</h2>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-green-800 mb-4">Rationale</h3>
                                    <p className="text-green-700 mb-4">
                                        {getCollateralSection('justification_recommendation')?.rationale}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-green-800 mb-2">Decision Factors:</h4>
                                        {renderArrayItems(getCollateralSection('justification_recommendation')?.decisionFactors, "text-green-600")}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-green-800 mb-4">Strategic Alignment</h3>
                                    <p className="text-green-700 mb-4">
                                        {getCollateralSection('justification_recommendation')?.strategicAlignment}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-green-800 mb-2">Comparative Advantages:</h4>
                                        {renderArrayItems(getCollateralSection('justification_recommendation')?.comparativeAdvantages, "text-green-600")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Testimonial */}
                {getCollateralSection('client_testimonial') && (
                    <section className="mb-12">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
                            <div className="text-center">
                                <div className="text-4xl text-blue-600 mb-4">"</div>
                                <blockquote className="text-xl text-gray-700 mb-6 italic">
                                    {getCollateralSection('client_testimonial')?.testimonialQuote}
                                </blockquote>
                                <div className="text-gray-600">
                                    <p className="font-semibold">
                                        {getCollateralSection('client_testimonial')?.clientName}
                                    </p>
                                    <p>
                                        {getCollateralSection('client_testimonial')?.clientTitle}, {' '}
                                        {getCollateralSection('client_testimonial')?.clientCompany}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Recipient Identification */}
                {/* {getCollateralSection('recipient_identification') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Target Audience</h2>
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-8">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-semibold text-indigo-800 mb-4">Decision Maker Profile</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <span className="font-semibold text-indigo-800">Influence Level:</span>
                                            <span className="ml-2 text-indigo-700">
                                                {getCollateralSection('recipient_identification')?.influenceLevel}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-indigo-800">Seniority Level:</span>
                                            <span className="ml-2 text-indigo-700">
                                                {getCollateralSection('recipient_identification')?.seniorityLevel}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-indigo-800">Stakeholder Type:</span>
                                            <span className="ml-2 text-indigo-700">
                                                {getCollateralSection('recipient_identification')?.stakeholderType}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-indigo-800 mb-4">Contact Preferences</h3>
                                    <p className="text-indigo-700 mb-4">
                                        {getCollateralSection('recipient_identification')?.contactPreferences}
                                    </p>
                                    <div>
                                        <h4 className="font-semibold text-indigo-800 mb-2">Department Affiliation:</h4>
                                        <p className="text-indigo-600">
                                            {getCollateralSection('recipient_identification')?.departmentAffiliation}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )} */}

                {/* Author Information */}
                {getCollateralSection('author_identification') && (
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">About Your Solutions Lead</h2>
                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                            <div className="flex items-start space-x-6">
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                        {getCollateralSection('author_identification')?.authorName}
                                    </h3>
                                    <p className="text-lg text-gray-600 mb-4">
                                        {getCollateralSection('author_identification')?.title}, {' '}
                                        {getCollateralSection('author_identification')?.company}
                                    </p>
                                    <p className="text-gray-700 mb-4">
                                        {getCollateralSection('author_identification')?.credentials}
                                    </p>
                                    <div className="space-y-2">
                                        <div>
                                            <span className="font-semibold text-gray-800">Contact:</span>
                                            <span className="ml-2 text-gray-700">
                                                {getCollateralSection('author_identification')?.contactInformation}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-gray-800">Experience:</span>
                                            <span className="ml-2 text-gray-700">
                                                {getCollateralSection('author_identification')?.relevantExperience}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Call to Action */}
                <section className="mb-12">
                    <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg p-8 text-white text-center">
                        <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
                        <p className="text-xl mb-6">
                            Let's discuss how we can help accelerate your business growth
                        </p>
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-semibold mb-2">Next Steps:</h3>
                                {getCollateralSection('recommendation_decision_required')?.nextSteps && (
                                    <ul className="text-green-100 space-y-1">
                                        {getCollateralSection('recommendation_decision_required')?.nextSteps.map((step, index) => (
                                            <li key={index}>• {step}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="pt-4">
                                <p className="text-green-100">
                                    <strong>Decision Deadline:</strong> {' '}
                                    {getCollateralSection('recommendation_decision_required')?.decisionDeadline}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center">
                        <div className="flex items-center space-x-4">
                            {(tenantLogoBlobUrl || collateralData.tenantlogo) && (
                                <img 
                                    src={tenantLogoBlobUrl || collateralData.tenantlogo} 
                                    alt={metadata.my_company_name || 'Company Logo'} 
                                    className="h-8 w-auto"
                                    crossOrigin="anonymous"
                                />
                            )}
                            <span className="text-gray-300">
                                © {new Date().getFullYear()} {metadata.my_company_name[0].toUpperCase() + metadata.my_company_name.slice(1) || 'Company'}. All rights reserved.
                            </span>
                        </div>
                       
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Collateral;